/**
 * Imperative snapshot store for deterministic visualization replay.
 * Advances on the frame-bus `replay` phase (before transfers/paint) so every
 * actor reads the same ownership/transfer snapshot for a given simTime.
 */
import {
  createReplayCoordinator,
  type ReplayCoordinator,
  type ReplayFrameSnapshot,
  type ReplayPlan,
} from '../utils/replay';
import { simulationFrameBus } from './simulationFrameBus';

type Listener = (snapshot: ReplayFrameSnapshot | null) => void;

class ReplaySnapshotStore {
  private plan: ReplayPlan | null = null;
  private coordinator: ReplayCoordinator | null = null;
  private snapshot: ReplayFrameSnapshot | null = null;
  private listeners = new Set<Listener>();
  private autoSeekEnabled = true;

  constructor() {
    simulationFrameBus.subscribe((simTime) => {
      if (this.autoSeekEnabled) this.seek(simTime);
    }, 'replay');
  }

  getPlan(): ReplayPlan | null {
    return this.plan;
  }

  getCoordinator(): ReplayCoordinator | null {
    return this.coordinator;
  }

  getSnapshot(): ReplayFrameSnapshot | null {
    return this.snapshot;
  }

  /**
   * Live playback has its own runtime scheduler. Its replay snapshot only needs
   * to advance when a new batched plan arrives, not on every animation frame.
   */
  setAutoSeekEnabled(enabled: boolean): void {
    this.autoSeekEnabled = enabled;
  }

  setPlan(plan: ReplayPlan | null, simTime = 0): void {
    if (this.plan === plan) {
      this.seek(simTime);
      return;
    }
    this.plan = plan;
    this.coordinator = plan ? createReplayCoordinator(plan) : null;
    this.snapshot = this.coordinator?.seek(simTime) ?? null;
    this.notify();
  }

  clear(): void {
    this.setPlan(null, 0);
  }

  seek(simTime: number): ReplayFrameSnapshot | null {
    if (!this.coordinator) return null;
    const next = this.coordinator.seek(simTime);
    if (next !== this.snapshot) {
      this.snapshot = next;
      this.notify();
    }
    return next;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.snapshot);
    }
  }
}

export const replaySnapshotStore = new ReplaySnapshotStore();
